import React, { useState, useEffect, useRef } from "react";
import { Input } from "../ui/input";
import { Label } from "../ui/label";

const COUNTDOWN_DURATION = 30; // seconds
const getStorageKey = (email) => `codeResendCountdownEnd_${email}`;

export function CodeInputWithResend({ 
  code, 
  setCode, 
  onResend, 
  email = "",
  label = "Confirmation Code",
  resendText = "Didn't receive a code?"
}) {
  const [resendDisabled, setResendDisabled] = useState(false);
  const [countdown, setCountdown] = useState(COUNTDOWN_DURATION);
  const timerRef = useRef(null);

  useEffect(() => {
    // Clear any existing timer when email changes
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    if (!email) {
      setResendDisabled(false);
      setCountdown(COUNTDOWN_DURATION);
      return;
    }

    const storageKey = getStorageKey(email);
    const storedEndTime = localStorage.getItem(storageKey);
    
    if (storedEndTime) {
      const endTime = parseInt(storedEndTime, 10);
      const now = Date.now();
      const remaining = Math.ceil((endTime - now) / 1000);
      
      if (remaining > 0) {
        setResendDisabled(true);
        setCountdown(remaining);
        
        timerRef.current = setInterval(() => {
          const now = Date.now();
          const remaining = Math.ceil((endTime - now) / 1000);
          
          if (remaining <= 0) {
            clearInterval(timerRef.current);
            localStorage.removeItem(storageKey);
            setResendDisabled(false);
            setCountdown(COUNTDOWN_DURATION);
          } else {
            setCountdown(remaining);
          }
        }, 1000);
      } else {
        localStorage.removeItem(storageKey);
        setResendDisabled(false);
        setCountdown(COUNTDOWN_DURATION);
      }
    } else {
      setResendDisabled(false);
      setCountdown(COUNTDOWN_DURATION);
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [email]);

  const handleResend = async (e) => {
    e.preventDefault();
    
    if (!email) {
      alert("Email is required to resend code");
      return;
    }
    
    try {
      await onResend();
      
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      
      const storageKey = getStorageKey(email);
      const endTime = Date.now() + (COUNTDOWN_DURATION * 1000);
      localStorage.setItem(storageKey, endTime.toString());
      
      setCountdown(COUNTDOWN_DURATION);
      setResendDisabled(true);
    
      timerRef.current = setInterval(() => {
        const now = Date.now();
        const remaining = Math.ceil((endTime - now) / 1000);
        
        if (remaining <= 0) {
          clearInterval(timerRef.current);
          localStorage.removeItem(storageKey);
          setResendDisabled(false);
          setCountdown(COUNTDOWN_DURATION);
        } else {
          setCountdown(remaining);
        }
      }, 1000);
    } catch (err) {
      alert("Error resending code: " + err.message);
    }
  };

  return (
    <div className="space-y-1">
      <Label htmlFor="code">{label}</Label>
      <Input
        id="code"
        type="text"
        value={code}
        onChange={(e) => setCode(e.target.value)}
        required
      />
      <div className="flex justify pt-1">
        <button
          type="button"
          onClick={handleResend}
          disabled={resendDisabled}
          className="text-sm text-blue-600 hover:text-blue-800 disabled:text-gray-400 disabled:cursor-not-allowed"
        >
          {resendDisabled ? `Resend in ${countdown}s` : resendText}
        </button>
      </div>
    </div>
  );
}